<script>
  import Icon from "@iconify/svelte";
  import { onMount } from "svelte";
  import { api } from "../lib/api.svelte.js";
  import { push } from "svelte-spa-router";
  import { t } from "../lib/i18n.svelte.js";
  import { addToast } from "../components/Toast.svelte";
  import Layout from "../components/Layout.svelte";

  let label = $state("");
  let allSites = $state(false);
  let submitting = $state(false);
  let created = $state(null);
  let baseDomain = $state("");

  // Sites list
  let sites = $state([]);
  let sitesLoading = $state(true);
  let siteSearch = $state("");
  let selectedSiteIds = $state(new Set());

  let filteredSites = $derived(
    siteSearch
      ? sites.filter(s =>
          (s.title || "").toLowerCase().includes(siteSearch.toLowerCase()) ||
          (s.slug || "").toLowerCase().includes(siteSearch.toLowerCase())
        )
      : sites
  );

  let selectedCount = $derived(selectedSiteIds.size);
  let primarySiteId = $derived(selectedSiteIds.size > 0 ? [...selectedSiteIds][0] : "");
  let primarySite = $derived(sites.find(s => s.id === primarySiteId));

  let magicLink = $derived.by(() => {
    if (!created) return "";
    const slug = created.site_slug || "";
    const token = created.invite_token || "";
    if (slug && baseDomain && token) return `${slug}.${baseDomain}?claim=${token}`;
    if (created.invite_url) return `${window.location.origin}${created.invite_url}`;
    return "";
  });

  let canSubmit = $derived(label.trim() && (allSites || selectedSiteIds.size > 0));

  onMount(async () => {
    try {
      const setup = await api.get("/setup");
      const bd = (setup?.env || []).find(e => e.name === "BASE_DOMAIN");
      if (bd) baseDomain = bd.value;
    } catch {}
    try {
      const res = await api.get("/sites?page=1&per_page=200&sort=title&order=asc");
      sites = res.data || [];
    } catch {}
    sitesLoading = false;
  });

  function toggleSite(id) {
    const next = new Set(selectedSiteIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    selectedSiteIds = next;
    if (next.size > 0) allSites = false;
  }

  function selectAll() {
    if (allSites) {
      allSites = false;
    } else {
      allSites = true;
      selectedSiteIds = new Set();
    }
  }

  async function submit() {
    if (!canSubmit || submitting) return;
    submitting = true;
    try {
      const siteId = allSites ? sites[0]?.id : primarySiteId;
      const res = await api.post("/viewers", {
        label,
        all_sites: allSites,
        site_id: siteId,
      });
      created = res;
    } catch (e) {
      addToast(e.message || t("viewer_create.failed"), "error");
    }
    submitting = false;
  }
</script>

<Layout>
  {#if created}
    <!-- Success state -->
    <div class="flex items-center justify-center" style="min-height: calc(100vh - 160px);">
      <div class="ui-card p-8 w-full max-w-md text-center">
        <Icon icon="mdi:check-circle" class="w-12 h-12 mx-auto text-green-500 mb-4" />
        <h2 class="text-lg font-semibold text-white mb-2">{t("viewer_create.created_title")}</h2>
        <p class="text-sm text-zinc-400 mb-4">{created.viewer?.label || label}</p>

        {#if magicLink}
          <div class="mb-4 text-left">
            <label class="block text-xs text-zinc-400 mb-1.5">{t("viewer_create.magic_link")}</label>
            <div class="flex gap-2">
              <input type="text" readonly value={magicLink} class="flex-1 h-10 px-3 bg-black/20 border border-white/10 rounded text-sm text-zinc-200 font-mono" />
              <button class="ui-btn !px-3" onclick={() => { navigator.clipboard.writeText(magicLink); addToast(t("common.copied"), "success"); }}>
                <Icon icon="mdi:content-copy" class="w-4 h-4" />
              </button>
            </div>
          </div>
        {/if}

        <div class="flex gap-3 justify-center">
          <button class="ui-btn" onclick={() => { created = null; label = ""; selectedSiteIds = new Set(); allSites = false; }}>
            {t("viewer_create.create_another")}
          </button>
          <button class="ui-btn-accent !px-6 !py-2.5 !rounded" onclick={() => push("/viewers")}>
            {t("viewer_create.done")}
          </button>
        </div>
      </div>
    </div>
  {:else}
    <!-- Create form: 2-column layout -->
    <div class="max-w-5xl mx-auto">
      <!-- Back -->
      <button type="button" class="text-xs text-zinc-500 hover:text-zinc-300 mb-4 inline-flex items-center gap-1" onclick={() => push("/viewers")}>
        <Icon icon="mdi:arrow-left" class="w-3.5 h-3.5" /> {t("site_create.back_to_viewers")}
      </button>

      <div class="flex gap-5" style="height: calc(100vh - 180px);">
        <!-- LEFT: Viewer details -->
        <div class="w-[340px] shrink-0">
          <div class="ui-card p-6">
            <div class="flex items-center gap-3 mb-6">
              <span class="ui-header-icon">
                <Icon icon="mdi:account-plus" class="w-[18px] h-[18px]" />
              </span>
              <div>
                <h1 class="text-lg font-semibold text-zinc-100">{t("viewer_create.title")}</h1>
                <p class="text-sm text-zinc-400">{t("viewer_create.subtitle")}</p>
              </div>
            </div>

            <div class="space-y-4">
              <div>
                <label class="block text-xs text-zinc-300 mb-1.5">{t("viewer_create.name_label")}</label>
                <input type="text" bind:value={label} class="w-full h-10 px-3 bg-black/20 border border-white/10 rounded text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-white/25" placeholder={t("viewer_create.name_placeholder")} autofocus />
              </div>

              <!-- Access summary -->
              <div class="p-3 rounded border border-white/[0.06] bg-white/[0.02]">
                <div class="flex items-center gap-2 text-xs text-zinc-400 mb-1">
                  <Icon icon="mdi:key-outline" class="w-3.5 h-3.5" />
                  {t("viewer_create.access")}
                </div>
                {#if allSites}
                  <p class="text-sm text-green-500 font-medium">{t("viewer_create.all_sites")}</p>
                {:else if selectedCount > 0}
                  <p class="text-sm text-zinc-200">{selectedCount} {selectedCount !== 1 ? t("viewer_create.sites") : t("viewer_create.site")} {t("viewer_create.selected")}</p>
                {:else}
                  <p class="text-sm text-zinc-500">{t("viewer_create.no_sites_selected")}</p>
                {/if}
              </div>

              <div class="flex gap-2 pt-2">
                <button class="ui-btn flex-1 !text-sm" onclick={() => push("/viewers")}>{t("common.cancel")}</button>
                <button
                  class="ui-btn-accent flex-1 !py-2.5 !text-sm !rounded !gap-2 {!canSubmit || submitting ? 'opacity-40' : ''}"
                  onclick={submit} disabled={!canSubmit || submitting}
                >
                  {#if submitting}
                    <Icon icon="mdi:loading" class="w-4 h-4 animate-spin" />
                  {:else}
                    <Icon icon="mdi:check" class="w-4 h-4" />
                  {/if}
                  {t("common.create")}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- RIGHT: Site selection -->
        <div class="flex-1 min-w-0 overflow-hidden">
          <div class="ui-card p-6 h-full flex flex-col overflow-hidden">
            <div class="flex items-center justify-between mb-4">
              <h2 class="text-sm font-medium text-zinc-200">{t("viewer_create.site_access")}</h2>
              <span class="text-xs text-zinc-500">{sites.length} {t("viewer_create.sites")}</span>
            </div>

            <!-- All sites toggle -->
            <button
              type="button"
              class="flex items-center justify-between w-full p-3 mb-3 rounded border transition-colors {allSites ? 'border-green-500/30 bg-green-500/5' : 'border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04]'}"
              onclick={selectAll}
            >
              <div class="flex items-center gap-3">
                <Icon icon="mdi:earth" class="w-5 h-5 {allSites ? 'text-green-500' : 'text-zinc-500'}" />
                <div class="text-left">
                  <p class="text-sm font-medium {allSites ? 'text-white' : 'text-zinc-300'}">{t("viewer_create.all_sites")}</p>
                  <p class="text-xs {allSites ? 'text-green-500/70' : 'text-zinc-600'}">{t("viewer_create.all_sites_desc")}</p>
                </div>
              </div>
              <div class="w-10 h-5 rounded-full transition-colors relative shrink-0 {allSites ? 'bg-green-500' : 'bg-zinc-700'}">
                <div class="w-4 h-4 rounded-full bg-white absolute top-0.5 transition-all {allSites ? 'left-5' : 'left-0.5'}"></div>
              </div>
            </button>

            {#if !allSites}
              <!-- Search -->
              <div class="mb-3">
                <input
                  type="text"
                  class="w-full h-9 px-3 bg-black/20 border border-white/10 rounded text-sm text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-white/25"
                  placeholder={t("viewer_create.search_sites")}
                  bind:value={siteSearch}
                />
              </div>

              <!-- Site list -->
              <div class="flex-1 min-h-0 overflow-y-auto -mx-2 px-2 space-y-1">
                {#if sitesLoading}
                  <div class="flex items-center justify-center py-12 text-sm text-zinc-500">
                    <Icon icon="mdi:loading" class="w-4 h-4 animate-spin mr-2" /> {t("viewer_create.loading_sites")}
                  </div>
                {:else if filteredSites.length === 0}
                  <div class="text-center py-12 text-sm text-zinc-500">
                    {siteSearch ? t("viewer_create.no_sites_match") : t("viewer_create.no_sites_available")}
                  </div>
                {:else}
                  {#each filteredSites as site (site.id)}
                    {@const selected = selectedSiteIds.has(site.id)}
                    <button
                      type="button"
                      class="flex items-center justify-between w-full p-3 rounded border transition-colors {selected ? 'border-green-500/30 bg-green-500/5' : 'border-transparent hover:bg-white/[0.03]'}"
                      onclick={() => toggleSite(site.id)}
                    >
                      <div class="flex items-center gap-3 min-w-0">
                        <Icon icon="mdi:web" class="w-4 h-4 shrink-0 {selected ? 'text-green-500' : 'text-zinc-600'}" />
                        <div class="text-left min-w-0">
                          <p class="text-sm truncate {selected ? 'text-white font-medium' : 'text-zinc-300'}">{site.title || site.slug}</p>
                          <p class="text-xs truncate {selected ? 'text-zinc-400' : 'text-zinc-600'}">{site.slug}.{baseDomain || "ui.localhost"}</p>
                        </div>
                      </div>
                      <div class="shrink-0 ml-3">
                        <div class="w-5 h-5 rounded border flex items-center justify-center transition-colors {selected ? 'bg-green-500 border-green-500' : 'border-white/20 bg-black/20'}">
                          {#if selected}
                            <Icon icon="mdi:check" class="w-3.5 h-3.5 text-white" />
                          {/if}
                        </div>
                      </div>
                    </button>
                  {/each}
                {/if}
              </div>
            {:else}
              <!-- Muted message when all sites is on -->
              <div class="flex-1 flex items-center justify-center">
                <div class="text-center py-12">
                  <Icon icon="mdi:earth" class="w-10 h-10 mx-auto text-zinc-700 mb-3" />
                  <p class="text-sm text-zinc-500">{t("viewer_create.all_sites_selected")}</p>
                  <p class="text-xs text-zinc-600 mt-1">{t("viewer_create.all_sites_toggle_hint")}</p>
                </div>
              </div>
            {/if}
          </div>
        </div>
      </div>
    </div>
  {/if}
</Layout>
